<!-- @format -->
<template>
	<header class="header">
		<div class="search">
			<!-- <el-input
				v-model="searchState.searchText"
				placeholder="搜索"
				prefix-icon="Search"
				class="search-input"
				@keyup.enter="submitForm"
			/> -->
			<el-button
				v-if="isMoreSearch"
				type="primary"
				icon="ArrowDown"
				link
				@click="searchState.isShowMoreSearchList = !searchState.isShowMoreSearchList"
			>
				更多筛选
			</el-button>
		</div>
		<div class="header-right">
			<!-- 设置前边的插槽 -->
			<slot name="extra"></slot>

			<el-popover
				v-if="useSlots().setting || isMoreColumns || isMoreExport"
				placement="bottom"
				:width="500"
				trigger="click"
			>
				<template #reference>
					<el-button link type="primary" icon="Setting" style="margin-left: 0px" />
				</template>
				<el-tabs v-model="activeName">
					<el-tab-pane v-if="isMoreColumns" label="定制列" name="col">
						<el-scrollbar max-height="400px">
							<pc-check-box-list
								v-model="tableStore.checkColumnList"
								:check-group-list="moreColumnList"
								@change="handleColumnChange"
							>
								<template #default="scope">
									<slot name="column-item" v-bind="scope"></slot>
								</template>
							</pc-check-box-list>
						</el-scrollbar>
					</el-tab-pane>

					<el-tab-pane v-if="isMoreExport" label="导出" name="export">
						<div class="export-header">
							<el-button type="primary" :loading="checkState.exportLoading" @click="onExport"> 导出 </el-button>
							<el-checkbox
								v-model="checkState.exportCheckAll"
								:indeterminate="checkState.exportIsIndeterminate"
								@change="handleCheckAllChange"
							>
								全选
							</el-checkbox>
						</div>
						<el-scrollbar max-height="400px">
							<pc-check-box-list
								v-model="checkState.checkExportList"
								:check-group-list="moreExportList"
								@change="handleCheckedCitiesChange"
							>
								<template #default="scope">
									<slot name="export-item" v-bind="scope"></slot>
								</template>
							</pc-check-box-list>
						</el-scrollbar>
					</el-tab-pane>
					<!-- 设置内部的插槽 -->
					<slot name="setting"></slot>
				</el-tabs>
			</el-popover>
		</div>
	</header>

	<el-form
		v-if="searchState.isShowMoreSearchList"
		ref="formRef"
		:model="searchState.searchListValue"
		label-position="right"
		label-width="auto"
		class="more-search-list"
		@submit.prevent
	>
		<el-row :gutter="20">
			<el-col v-for="item in searchState.checkSearchList" :key="item.code" :span="moreSearchSpan || 8">
				<el-form-item :label="item.label" :prop="item.code" class="more-search-list-from">
					<div class="more-search-list-from-item">
						<slot name="search-cpt-front" v-bind="item"></slot>
						<slot
							name="search-cpt"
							v-bind="item"
							:model-value="searchState.searchListValue[item.code]"
							:update:model-value="(e) => (searchState.searchListValue[item.code] = e)"
						>
							<component
								:is="getComponent(item.type)"
								v-model="searchState.searchListValue[item.code]"
								v-bind="item.props"
							/>
						</slot>
						<slot name="search-cpt-backend" v-bind="item"></slot>
					</div>
				</el-form-item>
			</el-col>
		</el-row>

		<div class="more-search-footer">
			<el-popover placement="bottom" :width="500" trigger="click">
				<template #reference>
					<el-button type="primary" icon="Plus" size="small" link append-to="true">添加更多</el-button>
				</template>
				<el-scrollbar max-height="400px">
					<pc-check-box-list v-model="searchState.checkSearchList" :check-group-list="moreSearchList">
						<template #default="scope">
							<slot name="search-item" v-bind="scope"></slot>
						</template>
					</pc-check-box-list>
				</el-scrollbar>
			</el-popover>
			<el-button type="primary" size="small" icon="Search" link @click="submitForm"> 查询 </el-button>
			<el-button size="small" link icon="Refresh" @click="resetForm(formRef)">
				<span>重置</span>
			</el-button>
		</div>
	</el-form>
</template>

<script setup lang="ts">
	import { useSlots } from 'vue';
	import PcCheckBoxList from './pc-check-box-list.vue';
	import { useTableStoreWithOut } from './store/index';
	import type { FormInstance } from 'element-plus';
	import { getComponent } from '@/components';

	interface CheckGroupList {
		code: string;
		label: string;
		[key: string]: any;
	}

	interface ComponentType {
		label: string;
		code: string;
		type: string;
		props?: Record<string, any>;
		[key: string]: any;
	}

	interface Props {
		isMoreSearch?: boolean; // 是否支持更多筛选
		isMoreColumns?: boolean; // 是否支持定制列
		isMoreExport?: boolean; // 是否支持导出
		moreSearchList?: ComponentType[]; // 支持放到更多筛选中的字段
		moreColumnList?: CheckGroupList[]; // 支持放到定制列的字段
		moreExportList?: CheckGroupList[]; // 支持导出的字段
		defaultSearchList?: ComponentType[]; // 默认更多筛选数据
		defaultColumnList?: CheckGroupList[]; // 默认定制列数据
		defaultExportList?: CheckGroupList[]; // 默认导出字段
		isShowMoreSearchList?: boolean; // 默认是否展示更多筛选
		moreSearchSpan?: number; // 更多筛选占比
		defaultSearchListValue?: Record<string, any>; // 更多筛选参数默认值
		name: string; // 组件全局唯一参数
		defaultSearchValue?: string; // 查询框默认值
	}

	interface SearchState {
		searchText: string;
		isShowMoreSearchList: boolean;
		searchListValue: Record<string, any>;
		checkSearchList: ComponentType[];
	}

	interface CheckState {
		checkExportList: CheckGroupList[];
		exportCheckAll: boolean;
		exportIsIndeterminate: boolean;
		exportLoading: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		isMoreSearch: false,
		isMoreColumns: false,
		isMoreExport: false,
		moreSearchList: () => [],
		moreColumnList: () => [],
		moreExportList: () => [],
		defaultSearchList: () => [],
		defaultColumnList: () => [],
		defaultExportList: () => [],
		isShowMoreSearchList: false,
		defaultSearchListValue: () => ({}),
		defaultSearchValue: '',
	});

	const emit = defineEmits(['onSearch', 'onExport', 'columnChange']);

	const tableStore = useTableStoreWithOut(props.name);

	const formRef = ref<FormInstance | null>(null);

	const searchState = ref<SearchState>({
		searchText: props.defaultSearchValue, // input查询框数据
		isShowMoreSearchList: props.isShowMoreSearchList, // 是否展示更多筛选框
		searchListValue: props.defaultSearchListValue, // 更多筛选数据
		checkSearchList: props.defaultSearchList, // 选中放到更多筛选中的字段
	});

	// //tab栏首页
	const activeName = ref('col');

	const checkState = ref<CheckState>({
		checkExportList: props.defaultExportList, // 导出复选框选中的数据
		exportCheckAll: false, //导出列全选框是否选中
		exportIsIndeterminate: false, // 导出列全选框是否选中状态
		exportLoading: false, // 导出按钮loading
	});

	// 更多筛选查询
	const submitForm = async () => {
		const obj = {};
		searchState.value.checkSearchList.forEach((item) => {
			obj[item.code] = searchState.value.searchListValue[item.code];
		});
		emit('onSearch', { searchText: searchState.value.searchText, ...obj });
	};

	// 重置更多筛选表单
	const resetForm = (formEl: FormInstance | null) => {
		if (!formEl) return;
		formEl.resetFields();
	};

	// 导出定制列全选事件
	const handleCheckAllChange = (val: boolean) => {
		checkState.value.checkExportList = val
			? props.moreExportList[0].children
				? props.moreExportList.reduce((arr, item) => [...arr, ...item.children], [])
				: [...props.moreExportList]
			: [];
		checkState.value.exportIsIndeterminate = false;
	};

	// 导出定制列change事件
	const handleCheckedCitiesChange = (value: string[] | CheckGroupList) => {
		const checkedCount = value.length;
		const maxLen = props.moreExportList[0]?.children
			? props.moreExportList.reduce((arr, item) => [...arr, ...item.children], []).length
			: props.moreExportList.length;

		checkState.value.exportCheckAll = checkedCount === maxLen;
		checkState.value.exportIsIndeterminate = checkedCount > 0 && checkedCount < maxLen;
	};

	// 导出定制列的导出
	const onExport = () => {
		checkState.value.exportLoading = true;
		emit('onExport', checkState.value.checkExportList, () => {
			checkState.value.exportLoading = false;
		});
	};

	const handleColumnChange = () => {
		emit('columnChange', tableStore.checkColumnList);
	};

	const init = () => {
		tableStore.reset();
	};

	init();

	watch(
		[() => props.defaultSearchList, () => props.defaultColumnList, () => props.defaultExportList],
		([defaultSearchList, defaultColumnList, defaultExportList]) => {
			// 设置默认查询条件
			searchState.value.checkSearchList = defaultSearchList;

			// 设置默认定制列
			tableStore.setState({ checkColumnList: defaultColumnList });

			// 设置默认导出选中项
			handleCheckedCitiesChange(defaultExportList);
			checkState.value.checkExportList = defaultExportList;
		},
		{
			immediate: true,
		},
	);

	defineExpose({ searchValue: searchState.value });
</script>

<style scoped lang="scss">
	.header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 5px 0;

		.search {
			display: flex;
			align-items: center;
			gap: 5px;
			padding-top: 10px;

			.search-input {
				width: 300px;
			}
		}

		.header-right {
			display: flex;
			gap: 5px;
		}
	}

	.more-search-list {
		border: 1px solid #dcdfe6;
		padding: 10px;

		margin-bottom: 5px;
		border-radius: 5px;

		.more-search-list-from {
			//border: 1px solid #000;
			display: flex;

			.more-search-list-from-item {
				width: 300px;
				display: flex;
				margin-left: 15px;
			}
		}

		.more-search-footer {
			display: flex;
			justify-content: flex-end;
		}
	}

	.export-header {
		display: flex;
		align-items: center;
		gap: 5px;
	}
</style>
